import React, { useEffect, useState, useMemo } from 'react'

import { useSelector } from 'react-redux'

import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { useToasts } from 'react-toast-notifications'
import randomColor from 'randomcolor'

import axiosInstance from '../../../../../services/config/AxiosIntance'

import Loader from '../../../../../components/Loader'
import DashboardItemTitle from '../../components/DashboardItemTitle'
import NoData from '../../components/NoData'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const TalentPoolChart = () => {
  const filters = useSelector((state) => state.dashboard.filters)
  const { addToast } = useToasts()

  const [loading, setLoading] = useState(false)
  const [totalApplicants, setTotalApplicants] = useState(0)
  const [chartData, setChartData] = useState({
    labels: [''],
    datasets: [],
  })

  useEffect(() => {
    setLoading(true)
    const queryParams = new URLSearchParams(filters).toString()

    axiosInstance
      .get(`/dashboard/talent-pool-chart?${queryParams}`)
      .then((res) => {
        const chartColors = Array.from(
          { length: res?.data?.result?.datasets?.length },
          (_) =>
            randomColor({
              luminosity: 'bright',
              hue: 'blue',
              format: 'hsla',
            })
        )

        setChartData((prev) => ({
          ...prev,
          labels: res.data.result.labels,
          datasets: res?.data?.result?.datasets.map((item, index) => ({
            label: item.name,
            data: item.data,
            backgroundColor: chartColors[index],
            borderColor: chartColors[index],
            borderWidth: 1,
            barPercentage: 1,
          })),
        }))
        setTotalApplicants(res.data.result.total_applicants)
      })
      .catch((err) => {
        addToast(
          err?.response?.message ||
          err?.response?.data?.message ||
          'Internal Server Error',
          {
            appearance: 'error',
            autoDismiss: true,
          }
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [filters])

  const options = useMemo(
    () => ({
      type: 'bar',
      data: chartData,
      maintainAspectRatio: false,
      plugins: {
        title: {
          color: 'black',
          display: true,
          align: 'start',
          position: 'top',
          text: `Talent Pool: ${totalApplicants}`,
          font: {
            size: 24,
            family: "'Lato', 'sans-serif'",
            weight: 300,
          },
        },
        datalabels: {
          formatter: (value) => (value === 0 ? '' : value),
          display: (ctx) => Boolean(ctx.dataset.data !== 0),
          color: 'black',
          anchor: 'center',
          clamp: true,
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
        },
        y: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            padding: 0.5,
          },
        },
      },
      responsive: true,
    }),
    [totalApplicants]
  )

  const component = loading ? (
    <Loader />
  ) : chartData.labels.length ? (
    <Bar options={options} data={chartData} plugins={[ChartDataLabels]} />
  ) : (
    <>
      <DashboardItemTitle title={`Talent Pool: ${totalApplicants}`} />
      <NoData />
    </>
  )

  return component
}

export default TalentPoolChart
